import React from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { X, Download } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { Invoice } from '@/types';
import { useInvoiceStore } from '@/store/invoiceStore';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({ 
  isOpen, 
  onClose, 
  invoice 
}) => {
  const { downloadInvoice } = useInvoiceStore();

  if (!invoice) return null;

  const handleDownload = async () => {
    await downloadInvoice(invoice._id);
  };

  const clientData = typeof invoice.clientId === 'object' 
    ? invoice.clientId 
    : { name: invoice.clientName };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Invoice for ${clientData.name}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Company Header */}
        <div className="flex justify-between items-start border-b pb-6">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">🐼</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Panda, Inc</h3>
              <p className="text-sm text-gray-600">www.website.com</p>
              <p className="text-sm text-gray-600">hello@email.com</p>
              <p className="text-sm text-gray-600">+91 00000 00000</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Business address</p>
            <p className="text-sm text-gray-600">City, State, IN - 000 000</p>
            <p className="text-sm text-gray-600">TAX ID 00XXXXX1234X0XX</p>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Billed to</h4>
            <p className="font-medium">{clientData.name}</p>
            {clientData.address && (
              <p className="text-sm text-gray-600">{clientData.address}</p>
            )}
            {clientData.gst && (
              <p className="text-sm text-gray-600">GST: {clientData.gst}</p>
            )}
            {clientData.phone && (
              <p className="text-sm text-gray-600">{clientData.phone}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Invoice number</p>
            <p className="font-medium">{invoice.invoiceNumber}</p>
            <p className="text-sm text-gray-600 mt-2">Invoice date</p>
            <p className="font-medium">{formatDate(invoice.generatedDate || invoice.createdAt)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600">Subject</p>
            <p className="font-medium">Design System</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Due date</p>
            <p className="font-medium">{formatDate(invoice.dueDate!)}</p>
          </div>
        </div>

        {/* Invoice Amount */}
        <div className="text-right">
          <p className="text-sm text-gray-600">Invoice of (USD)</p>
          <p className="text-3xl font-bold">{formatCurrency(invoice.amount)}</p>
        </div>

        {/* Items Table */}
        <div>
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-2 text-sm font-medium text-gray-600">ITEM DETAIL</th>
                <th className="text-center py-2 text-sm font-medium text-gray-600">QTY</th>
                <th className="text-right py-2 text-sm font-medium text-gray-600">RATE</th>
                <th className="text-right py-2 text-sm font-medium text-gray-600">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-3">
                    <p className="font-medium">{item.description}</p>
                  </td>
                  <td className="py-3 text-center">{item.quantity}</td>
                  <td className="py-3 text-right">{formatCurrency(item.rate)}</td>
                  <td className="py-3 text-right">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="py-3 text-right font-medium">Subtotal</td>
                <td className="py-3 text-right">{formatCurrency(invoice.subtotal)}</td>
              </tr>
              {invoice.taxAmount > 0 && (
                <tr>
                  <td colSpan={3} className="py-3 text-right font-medium">Tax (10%)</td>
                  <td className="py-3 text-right">{formatCurrency(invoice.taxAmount)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={3} className="py-3 text-right font-bold">Total</td>
                <td className="py-3 text-right font-bold">{formatCurrency(invoice.amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Terms and Notes */}
        {(invoice.notes || invoice.termsAndConditions) && (
          <div className="border-t pt-4">
            {invoice.notes && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                <p className="text-sm text-gray-600">{invoice.notes}</p>
              </div>
            )}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Terms & Conditions</h4>
              <p className="text-sm text-gray-600">
                {invoice.termsAndConditions || 'Please pay within 15 days of receiving this invoice.'}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </div>
      </div>
    </Modal>
  );
};